import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { CheckCircle2, XCircle, Loader2, FileText, Eraser, PenLine, AlertTriangle, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PublicProposalView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flowState, setFlowState] = useState("review");
  const [rejectReason, setRejectReason] = useState("");
  const [hasSigned, setHasSigned] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/proposals/public/${token}`)
      .then(r => { if (!r.ok) throw new Error("Proposal not found"); return r.json(); })
      .then(d => {
        setData(d);
        if (d.status === "accepted") setFlowState("approved");
        else if (d.status === "rejected") setFlowState("rejected");
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const clearSig = useCallback(() => { sigRef.current?.clear(); setHasSigned(false); }, []);

  const handleApprove = async () => {
    // Check canvas directly instead of relying only on state
    const isEmpty = sigRef.current?.isEmpty?.() ?? true;
    if (isEmpty) {
      setError("Please sign above before accepting");
      return;
    }
    setFlowState("submitting");
    setError(null);
    try {
      const sigData = sigRef.current.toDataURL("image/png");
      const res = await fetch(`${API}/api/proposals/public/${token}/approve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_data: sigData }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Failed"); }
      setData(prev => ({ ...prev, status: "accepted", signature_data: sigData }));
      setFlowState("approved");
    } catch (e) { setError(e.message); setFlowState("signing"); }
  };

  const handleReject = async () => {
    setFlowState("submitting");
    setError(null);
    try {
      const res = await fetch(`${API}/api/proposals/public/${token}/reject`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rejectReason }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Failed"); }
      setData(prev => ({ ...prev, status: "rejected", rejection_reason: rejectReason }));
      setFlowState("rejected");
    } catch (e) { setError(e.message); setFlowState("rejecting"); }
  };

  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };

  if (loading) return (
    <div style={S.page}><div style={S.center}><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#64748b" }} /></div></div>
  );
  if (error && !data) return (
    <div style={S.page}><div style={{ ...S.card, textAlign: "center", padding: 40 }}>
      <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: "#ef4444" }} />
      <p style={{ color: "#ef4444", fontSize: 15, fontWeight: 500 }}>{error}</p>
    </div></div>
  );

  const primary = "#0046BE";

  // Accepted
  if (flowState === "approved") return (
    <div style={S.page} data-testid="proposal-approved-screen">
      <div style={{ ...S.card, textAlign: "center", padding: "48px 32px" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <CheckCircle2 style={{ width: 36, height: 36, color: "#059669" }} />
        </div>
        <h1 style={{ ...S.h1, marginBottom: 8 }}>Proposal Accepted</h1>
        <p style={S.sub}>Thank you! Your acceptance for <strong>{data.title}</strong> has been recorded.</p>
        <div style={{ ...S.totalBox, margin: "24px auto 0", maxWidth: 240 }}>
          <span style={S.totalLabel}>Accepted Total</span>
          <span style={{ ...S.totalValue, color: "#059669" }}>{fmt(data.total)}</span>
        </div>
        {data.signature_data && (
          <div style={{ marginTop: 24 }}>
            <p style={{ ...S.label, textAlign: "center" }}>Your Signature</p>
            <img src={data.signature_data} alt="Signature" style={{ maxWidth: 220, height: 80, objectFit: "contain", margin: "8px auto", display: "block", opacity: 0.8 }} />
          </div>
        )}
      </div>
    </div>
  );

  // Rejected
  if (flowState === "rejected") return (
    <div style={S.page} data-testid="proposal-rejected-screen">
      <div style={{ ...S.card, textAlign: "center", padding: "48px 32px" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <XCircle style={{ width: 36, height: 36, color: "#dc2626" }} />
        </div>
        <h1 style={{ ...S.h1, marginBottom: 8 }}>Proposal Declined</h1>
        <p style={S.sub}>You've declined the proposal: <strong>{data.title}</strong>.</p>
        {(data.rejection_reason || rejectReason) && (
          <p style={{ ...S.sub, marginTop: 12, fontStyle: "italic" }}>"{data.rejection_reason || rejectReason}"</p>
        )}
      </div>
    </div>
  );

  // Main review
  return (
    <div style={S.page} data-testid="public-proposal-page">
      <div style={S.card}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", margin: 0, fontFamily: S.fontHead }}>Fieldz AI</h2>
            <p style={{ fontSize: 12, color: "#94a3b8", margin: "2px 0 0" }}>Custom Package Proposal</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: primary, marginBottom: 4 }}>Proposal</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", fontFamily: S.fontHead }}>{data.title}</div>
          </div>
        </div>

        {/* Recipient + Dates */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28, paddingBottom: 20, borderBottom: "1px solid #e2e8f0" }}>
          <div>
            <p style={S.label}>Prepared For</p>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", margin: "4px 0 0" }}>{data.recipient_name}</p>
            {data.recipient_company && <p style={{ fontSize: 13, color: "#64748b", margin: "2px 0 0" }}>{data.recipient_company}</p>}
            {data.recipient_email && <p style={{ fontSize: 13, color: "#64748b" }}>{data.recipient_email}</p>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ marginBottom: 8 }}>
              <p style={S.label}>Date</p>
              <p style={{ fontSize: 13, color: "#0f172a", margin: "3px 0 0", fontWeight: 500 }}>{fmtDate(data.created_at)}</p>
            </div>
            {data.valid_until && (
              <div>
                <p style={S.label}>Valid Until</p>
                <p style={{ fontSize: 13, color: "#0f172a", margin: "3px 0 0", fontWeight: 500 }}>{fmtDate(data.valid_until)}</p>
              </div>
            )}
          </div>
        </div>

        {data.custom_intro && (
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{data.custom_intro}</p>
          </div>
        )}

        {/* Items Table */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: "0 16px", paddingBottom: 10, borderBottom: "2px solid #e2e8f0" }}>
            <span style={S.thLabel}>Description</span>
            <span style={{ ...S.thLabel, textAlign: "right" }}>Qty</span>
            <span style={{ ...S.thLabel, textAlign: "right" }}>Price</span>
            <span style={{ ...S.thLabel, textAlign: "right" }}>Total</span>
          </div>
          {(data.items || []).map((item, i) => {
            const lineTotal = (item.quantity || 1) * (item.unit_price || 0) * (1 - (item.discount || 0) / 100);
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: "0 16px", padding: "12px 0", borderBottom: "1px solid #f1f5f9" }}>
                <div>
                  <span style={{ fontSize: 14, color: "#0f172a", fontWeight: 500 }}>{item.description}</span>
                  {item.discount > 0 && <span style={{ fontSize: 11, color: "#f97316", marginLeft: 8 }}>({item.discount}% off)</span>}
                </div>
                <span style={{ fontSize: 14, color: "#64748b", textAlign: "right", minWidth: 40 }}>{item.quantity}</span>
                <span style={{ fontSize: 14, color: "#64748b", textAlign: "right", minWidth: 70 }}>{fmt(item.unit_price)}</span>
                <span style={{ fontSize: 14, color: "#0f172a", fontWeight: 600, textAlign: "right", minWidth: 80 }}>{fmt(lineTotal)}</span>
              </div>
            );
          })}
        </div>

        {/* Totals */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 28 }}>
          <div style={{ minWidth: 240 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
              <span style={{ fontSize: 13, color: "#64748b" }}>Subtotal</span>
              <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 500 }}>{fmt(data.subtotal)}</span>
            </div>
            {data.discount_total > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                <span style={{ fontSize: 13, color: "#64748b" }}>Discount</span>
                <span style={{ fontSize: 13, color: "#ef4444" }}>-{fmt(data.discount_total)}</span>
              </div>
            )}
            {data.tax_rate > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                <span style={{ fontSize: 13, color: "#64748b" }}>Tax ({data.tax_rate}%)</span>
                <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 500 }}>{fmt(data.tax_amount)}</span>
              </div>
            )}
            <div style={S.totalBox}>
              <span style={S.totalLabel}>Total</span>
              <span style={S.totalValue}>{fmt(data.total)}</span>
            </div>
          </div>
        </div>

        {data.notes && (
          <div style={{ marginBottom: 20, padding: 16, background: "#f8fafc", borderRadius: 8 }}>
            <p style={{ ...S.label, marginBottom: 6 }}>Notes</p>
            <p style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{data.notes}</p>
          </div>
        )}

        {data.payment_terms && (
          <div style={{ marginBottom: 20, padding: 16, background: "#f8fafc", borderRadius: 8 }}>
            <p style={{ ...S.label, marginBottom: 6 }}>Payment Terms</p>
            <p style={{ fontSize: 13, color: "#475569", lineHeight: 1.6 }}>{data.payment_terms}</p>
          </div>
        )}

        {/* Download PDF */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <a href={`${API}/api/proposals/public/${token}/pdf`} target="_blank" rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, color: primary, fontSize: 13, fontWeight: 500 }}>
            <Download style={{ width: 14, height: 14 }} /> Download PDF
          </a>
        </div>

        {/* Action Buttons */}
        {flowState === "review" && (
          <div style={{ display: "flex", gap: 12, justifyContent: "center", paddingTop: 20, borderTop: "1px solid #e2e8f0" }}>
            <button onClick={() => setFlowState("rejecting")} style={{ ...S.btn, background: "#fff", color: "#dc2626", border: "1px solid #fecaca" }} data-testid="proposal-decline-btn">
              <XCircle style={{ width: 16, height: 16 }} /> Decline
            </button>
            <button onClick={() => setFlowState("signing")} style={{ ...S.btn, background: primary, color: "#fff" }} data-testid="proposal-accept-btn">
              <PenLine style={{ width: 16, height: 16 }} /> Accept & Sign
            </button>
          </div>
        )}

        {/* Signing */}
        {flowState === "signing" && (
          <div style={{ paddingTop: 20, borderTop: "1px solid #e2e8f0" }} data-testid="proposal-signing-section">
            <p style={{ ...S.label, marginBottom: 8 }}>Sign below to accept this proposal</p>
            {error && (
              <p style={{ color: "#dc2626", fontSize: 13, marginBottom: 8 }}>{error}</p>
            )}
            <div style={{ border: "2px dashed #cbd5e1", borderRadius: 8, position: "relative", background: "#fff", touchAction: "none" }}>
              <SignatureCanvas ref={sigRef} penColor="#0f172a"
                canvasProps={{ width: 500, height: 160, style: { width: "100%", height: 160, touchAction: "none" } }}
                onBegin={() => setHasSigned(true)}
                onEnd={() => setHasSigned(true)} />
              <button onClick={clearSig} style={{ position: "absolute", top: 8, right: 8, background: "#f1f5f9", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#64748b" }}>
                <Eraser style={{ width: 12, height: 12 }} /> Clear
              </button>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => { setFlowState("review"); setError(null); }} style={{ ...S.btn, background: "#fff", color: "#64748b", border: "1px solid #e2e8f0" }}>Back</button>
              <button onClick={handleApprove} style={{ ...S.btn, background: "#059669", color: "#fff" }} data-testid="proposal-confirm-accept-btn">
                <CheckCircle2 style={{ width: 16, height: 16 }} /> Confirm & Accept
              </button>
            </div>
          </div>
        )}

        {/* Rejecting */}
        {flowState === "rejecting" && (
          <div style={{ paddingTop: 20, borderTop: "1px solid #e2e8f0" }} data-testid="proposal-rejecting-section">
            <p style={{ ...S.label, marginBottom: 8 }}>Reason for declining (optional)</p>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Tell us why you're declining..." rows={3}
              style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12, fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setFlowState("review")} style={{ ...S.btn, background: "#fff", color: "#64748b", border: "1px solid #e2e8f0" }}>Back</button>
              <button onClick={handleReject} style={{ ...S.btn, background: "#dc2626", color: "#fff" }} data-testid="proposal-confirm-decline-btn">
                <XCircle style={{ width: 16, height: 16 }} /> Confirm Decline
              </button>
            </div>
          </div>
        )}

        {flowState === "submitting" && (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: primary }} />
            <p style={{ color: "#64748b", fontSize: 14, marginTop: 12 }}>Processing...</p>
          </div>
        )}
      </div>
    </div>
  );
};

const S = {
  fontHead: "'Inter', system-ui, -apple-system, sans-serif",
  page: { minHeight: "100vh", background: "#f8fafc", padding: "24px 16px", display: "flex", justifyContent: "center", alignItems: "flex-start" },
  card: { background: "#fff", borderRadius: 12, padding: "32px 28px", maxWidth: 680, width: "100%", marginTop: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)" },
  center: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" },
  h1: { fontSize: 22, fontWeight: 700, color: "#0f172a", fontFamily: "'Inter', system-ui, sans-serif" },
  sub: { fontSize: 14, color: "#64748b", lineHeight: 1.5 },
  label: { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8" },
  thLabel: { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8" },
  totalBox: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "2px solid #e2e8f0", marginTop: 4 },
  totalLabel: { fontSize: 15, fontWeight: 700, color: "#0f172a" },
  totalValue: { fontSize: 20, fontWeight: 700, color: "#0046BE" },
  btn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer", border: "none", fontFamily: "inherit" },
};

export default PublicProposalView;
